import { Controller } from '@hotwired/stimulus';

/**
 * Card modal - the card opened over the board. Locks the page scroll while open, focuses the dialog,
 * closes with Escape (a focused field or an open panel gets the first Escape) and moves focus on request.
 * Targets: dialog - the focusable dialog | close - link back to the board
 * @example
 * <div data-controller="card-modal" data-action="keydown@window->card-modal#keydown">
 *   <div data-card-modal-target="dialog" tabindex="-1">
 *     <a data-card-modal-target="close" href="/boards/1">Close</a>
 *     <a href="#note" data-action="click->card-modal#focus" data-card-modal-selector-param="#note">Note</a>
 *   </div>
 * </div>
 */
export default class extends Controller {
  static targets = ['dialog', 'close'];

  connect () {
    document.body.classList.add('has-card-modal');
    if (this.hasDialogTarget) this.dialogTarget.focus({ preventScroll: true });
  }

  disconnect () {
    document.body.classList.remove('has-card-modal');
  }

  keydown (event) {
    if (event.key !== 'Escape' || event.defaultPrevented) return;
    // An open preview (lightbox) closes first; the browser does that for a modal <dialog>.
    if (this.element.querySelector('dialog[open]')) return;

    const field = event.target.closest?.('input, textarea, select, [contenteditable="true"]');
    if (field) {
      field.blur();
      return;
    }

    const openPanel = this.element.querySelector('details[open]');
    if (openPanel) {
      openPanel.open = false;
      return;
    }

    if (this.hasCloseTarget) this.closeTarget.click();
  }

  // Moves focus to the element matching the selector param (a link or a freshly opened <details> triggers it).
  focus (event) {
    const trigger = event.currentTarget;
    if (trigger.tagName === 'DETAILS' && !trigger.open) return;

    const target = this.element.querySelector(event.params.selector);
    if (!target) return;

    if (trigger.tagName === 'A') event.preventDefault();
    target.focus();
    target.scrollIntoView({ block: 'nearest' });
  }
}
